import {
  EDP_AIX_ID,
  PHILOSOPHIES,
  AI_ROLES,
  INTERACTION_MODES,
  ENTRY_POINTS,
  REC_CATEGORIES,
  CONTEXT_FIELDS,
  APPROVAL_ACTIONS,
  DEMO_RECOMMENDATIONS,
  DEMO_SIMULATIONS,
  buildAIXDashboard,
} from '../../aix/framework';
import {
  Button, Card, KpiCard, AIRecommendationCard, ReasoningPanel, ApprovalCard, CopilotPanel,
} from '../../components/ui';

export function AIXExperiencePage() {
  const dashboard = buildAIXDashboard();
  const demoRec = DEMO_RECOMMENDATIONS[0];

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ai-600">{EDP_AIX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise AI Experience</h1>
        <p className="mt-1 text-sm text-slate-500">
          Copilot framework, human-AI collaboration, context engine, recommendations, simulations, and approval standards — governing every AI interaction.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="AI Roles" value={dashboard.active_roles.toString()} />
        <KpiCard label="Interaction Modes" value={dashboard.interaction_modes.toString()} />
        <KpiCard label="Open Recommendations" value={dashboard.open_recommendations.toString()} />
        <KpiCard label="Pending Approvals" value={dashboard.pending_approvals.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">AIX Philosophy</h2>
        <p className="mt-1 text-sm text-slate-500">Every AI interaction SHALL reference at least one philosophy pillar (EDP-00003 §2).</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Interaction Modes</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {INTERACTION_MODES.map((m) => (
              <span key={m.id} className="rounded-full bg-ai-50 px-3 py-1 text-xs font-medium text-ai-700 dark:bg-ai-950 dark:text-ai-300">
                {m.label}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Entry Points</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ENTRY_POINTS.map((e) => (
              <li key={e.id}>
                • {e.label}
                {e.shortcut && <span className="ml-2 text-xs text-slate-400">({e.shortcut})</span>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Context Engine Fields</h2>
        <p className="mt-1 text-sm text-slate-500">Automatically assembled before every AI response — user SHOULD NOT repeat context.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {CONTEXT_FIELDS.map((f) => (
            <span key={f.id} className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-400">
              {f.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Roles</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {AI_ROLES.map((r) => (
            <Card key={r.role} padding="sm">
              <p className="font-medium">{r.label}</p>
              {r.description && <p className="mt-1 text-xs text-slate-500">{r.description}</p>}
              {r.modules && (
                <p className="mt-2 text-[10px] text-slate-400">{r.modules.join(', ')}</p>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Recommendation Categories</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {REC_CATEGORIES.map((c) => (
            <span key={c.id} className="rounded-md bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">
              <span className="font-medium">{c.priority}</span> · {c.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Live Component Preview</h2>
        <p className="mt-1 text-sm text-slate-500">EDP-00003 UI components built on EDP-00002 design tokens.</p>
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <AIRecommendationCard
              title={demoRec.title}
              description={demoRec.explanation.what}
              confidence={demoRec.explanation.confidence}
              expectedImpact={demoRec.explanation.expected_impact}
              requiresApproval={demoRec.requires_approval}
              type={demoRec.category}
            />
            <ReasoningPanel reasoning={demoRec.reasoning} confidence={demoRec.explanation.confidence} />
          </div>
          <ApprovalCard recommendation={DEMO_RECOMMENDATIONS[1]} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Copilot Panel</h2>
        <div className="mt-4 max-w-md">
          <CopilotPanel
            recommendations={DEMO_RECOMMENDATIONS}
            contextSummary="AudioPro · Amazon US · Revenue Ops · last 30 days"
          />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Simulations</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {DEMO_SIMULATIONS.map((sim) => (
            <Card key={sim.id} padding="sm">
              <p className="text-xs font-medium uppercase text-slate-500">{sim.type.replace(/_/g, ' ')}</p>
              <p className="mt-1 font-medium">{sim.title}</p>
              {sim.projected_impact && (
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{sim.projected_impact}</p>
              )}
              <p className="mt-2 text-xs text-slate-400">Confidence: {Math.round(sim.confidence * 100)}% · {sim.status}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Approval Actions</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {APPROVAL_ACTIONS.map((a) => (
            <Button key={a.id} variant="secondary" size="sm">{a.label}</Button>
          ))}
        </div>
      </section>

      <Card className="border-ai-200 bg-ai-50/30 dark:border-ai-900 dark:bg-ai-950/20">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Developer Implementation</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Registry: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/aix/</code> · Frontend:{' '}
          <code className="rounded bg-white/60 px-1 dark:bg-slate-800">apps/web/src/aix/framework.ts</code> ·
          Runtime: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">services/copilot/</code>
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00003_ENTERPRISE_AI_EXPERIENCE.md</code>
        </p>
      </Card>
    </div>
  );
}
